import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ShieldPlus, Search, History, ShieldAlert, Users, KeyRound, Copy } from "lucide-react";
import { api } from "@/lib/api";

const ROLES = ["user", "admin", "super_admin"];
const ROLE_STYLE = {
  user: "bg-zinc-700 text-zinc-200",
  admin: "bg-emerald-600 text-white",
  super_admin: "bg-amber-500 text-black",
};

function when(iso) {
  try { return new Date(iso).toLocaleString("id-ID", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

export const RoleManager = () => {
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [busy, setBusy] = useState(null);
  const [confirm, setConfirm] = useState(null); // { user, role }
  const [selected, setSelected] = useState(() => new Set());
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [detail, setDetail] = useState(null); // { user, data|null }
  const [auditQ, setAuditQ] = useState("");
  const [auditFrom, setAuditFrom] = useState("");
  const [auditTo, setAuditTo] = useState("");
  const [attempts, setAttempts] = useState([]);
  const [subs, setSubs] = useState(null);
  const [runtime, setRuntime] = useState(null);
  const [findEmail, setFindEmail] = useState("");
  const [found, setFound] = useState(null); // null | {found, email, role, ...}
  const [resetInfo, setResetInfo] = useState(null); // {email, link, emailConfigured}

  const findUser = async () => {
    if (!findEmail.trim()) return;
    setBusy("find");
    setResetInfo(null);
    try {
      setFound(await api.adminFindUser(findEmail.trim()));
    } catch (e) {
      toast.error(e.message || "Gagal mencari user.");
      setFound(null);
    } finally {
      setBusy(null);
    }
  };

  const makeResetLink = async () => {
    if (!found?.email) return;
    setBusy("reset");
    try {
      const r = await api.adminMakeResetLink(found.email);
      setResetInfo(r);
      toast.success("Tautan reset dibuat (berlaku 60 menit, sekali pakai).");
    } catch (e) {
      toast.error(e.message || "Gagal membuat tautan reset.");
    } finally {
      setBusy(null);
    }
  };

  const copyResetLink = async () => {
    try {
      await navigator.clipboard.writeText(resetInfo.link);
      toast.success("Tautan disalin — kirim ke user.");
    } catch {
      toast.error("Gagal menyalin. Salin manual dari kolom.");
    }
  };


  const loadUsers = useCallback((query) => {
    api.adminListUsers(query).then((r) => setUsers(r.users || [])).catch(() => {});
  }, []);
  const loadAudit = useCallback(() => {
    api.adminRoleAudit().then((r) => setAudit(r.entries || [])).catch(() => {});
  }, []);
  const loadAttempts = useCallback(() => {
    api.adminAccessAttempts().then((r) => setAttempts(r.attempts || [])).catch(() => {});
  }, []);
  const loadSubs = useCallback(() => {
    api.adminActiveSubscriptions().then(setSubs).catch(() => {});
  }, []);
  const loadRuntime = useCallback(() => {
    api.adminRuntimeInfo().then(setRuntime).catch(() => {});
  }, []);

  const revokePremium = async (s) => {
    if (!window.confirm(`Cabut akses Premium untuk ${s.email}? Akses akan langsung berakhir.`)) return;
    try {
      await api.adminRevokePremium(s.userId);
      toast.success(`Premium ${s.email} dicabut.`);
      loadSubs();
    } catch (e) {
      toast.error(e.message || "Gagal mencabut premium.");
    }
  };

  useEffect(() => { loadUsers(); loadAudit(); loadAttempts(); loadSubs(); loadRuntime(); }, [loadUsers, loadAudit, loadAttempts, loadSubs, loadRuntime]);

  const changeRole = async (u, role) => {
    if (role === u.role) return;
    setConfirm({ user: u, role });
  };

  const doChange = async () => {
    if (!confirm) return;
    const { user: u, role } = confirm;
    setBusy(u.id);
    setConfirm(null);
    try {
      await api.adminSetRole(u.id, role);
      toast.success(`Peran ${u.email} → ${role}`);
      loadUsers(q);
      loadAudit();
    } catch (e) {
      toast.error(e.message || "Gagal mengubah peran");
    } finally {
      setBusy(null);
    }
  };

  const toggleSelect = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const setTest = async (ids, isTest) => {
    try {
      await api.adminSetTest(ids, isTest);
      toast.success(isTest ? `${ids.length} akun ditandai Test.` : "Status Test dilepas.");
      setSelected(new Set());
      setBulkConfirm(false);
      loadUsers(q);
    } catch (e) { toast.error(e.message || "Gagal"); }
  };

  const grantTestPremium = async (u) => {
    const ans = window.prompt(`Beri TEST PREMIUM ke ${u.email} berapa hari?`, "7");
    if (ans === null) return;
    const n = parseInt(ans, 10);
    if (!Number.isFinite(n) || n <= 0) { toast.error("Jumlah hari tidak valid."); return; }
    try {
      await api.adminGrantTestPremium(u.id, n);
      toast.success(`TEST PREMIUM ${n} hari diberikan.`);
      loadUsers(q);
    } catch (e) { toast.error(e.message || "Gagal"); }
  };

  const openDetail = async (u) => {
    setDetail({ user: u, data: null });
    try {
      const d = await api.adminUserSubscription(u.id);
      setDetail({ user: u, data: d });
    } catch (e) {
      toast.error(e.message || "Gagal memuat detail");
      setDetail(null);
    }
  };

  const revokeTestPremium = async (u) => {
    if (!window.confirm(`Cabut TEST PREMIUM ${u.email} sekarang? Akses premium & komunitas langsung terkunci.`)) return;
    try {
      await api.adminRevokeTestPremium(u.id);
      toast.success("TEST PREMIUM dicabut.");
      const d = await api.adminUserSubscription(u.id);
      setDetail({ user: u, data: d });
      loadUsers(q);
    } catch (e) { toast.error(e.message || "Gagal"); }
  };

  const filteredAudit = audit.filter((a) => {
    if (auditQ) {
      const s = auditQ.toLowerCase();
      if (!(a.actorEmail || "").toLowerCase().includes(s) && !(a.targetEmail || "").toLowerCase().includes(s)) return false;
    }
    const d = a.createdAt ? a.createdAt.slice(0, 10) : "";
    if (auditFrom && d < auditFrom) return false;
    if (auditTo && d > auditTo) return false;
    return true;
  });

  return (
    <div className="mt-8" data-testid="role-manager">
      <div className="mb-3 flex items-center gap-2">
        <ShieldPlus className="h-5 w-5 text-amber-400" />
        <h2 className="font-heading text-lg font-extrabold text-white">Manajemen Peran</h2>
        <span className="rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-bold text-amber-300">SUPER ADMIN</span>
      </div>

      {/* Runtime status: payment mode + DB */}
      {runtime && (
        <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="runtime-info">
          <span
            data-testid="payment-mode-badge"
            className={`rounded-full px-3 py-1 text-[11px] font-bold ${
              runtime.payment?.isProduction
                ? "bg-emerald-500/20 text-emerald-300"
                : "bg-rose-500/20 text-rose-300"
            }`}
          >
            Pembayaran: {runtime.payment?.mode}
          </span>
          <span
            data-testid="db-status-badge"
            className={`rounded-full px-3 py-1 text-[11px] font-bold ${
              runtime.database?.ok ? "bg-emerald-500/20 text-emerald-300" : "bg-rose-500/20 text-rose-300"
            }`}
          >
            Database: {runtime.database?.ok ? "Terhubung" : "Terputus"}
          </span>
          <span
            data-testid="email-status-badge"
            className={`rounded-full px-3 py-1 text-[11px] font-bold ${
              runtime.email?.configured ? "bg-emerald-500/20 text-emerald-300" : "bg-rose-500/20 text-rose-300"
            }`}
          >
            Email: {runtime.email?.configured ? "Aktif" : "Nonaktif (RESEND_API_KEY kosong)"}
          </span>
          {!runtime.payment?.isProduction && (
            <span className="text-[11px] text-rose-300">
              ⚠ Masih mode Sandbox — pembayaran bersifat simulasi (tidak memotong uang nyata).
            </span>
          )}
        </div>
      )}

      {/* Bantu user: cari akun + tautan reset kata sandi manual */}
      <div className="mb-4 rounded-2xl border border-zinc-700 bg-zinc-900 p-4" data-testid="reset-tool">
        <div className="mb-2 flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-emerald-400" />
          <p className="font-heading text-sm font-bold text-white">Cari User & Reset Kata Sandi Manual</p>
        </div>
        <p className="mb-2 text-[11px] text-zinc-400">
          Untuk user yang tidak menerima email reset. Tautan bisa Anda salin & kirim manual (WhatsApp/dll).
        </p>
        <div className="flex gap-2">
          <input
            data-testid="reset-email-input"
            value={findEmail}
            onChange={(e) => setFindEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") findUser(); }}
            placeholder="email user, mis. nama@gmail.com"
            className="h-10 flex-1 rounded-xl border border-zinc-700 bg-zinc-950 px-3 text-sm text-white outline-none"
          />
          <button data-testid="reset-find-btn" onClick={findUser} disabled={busy === "find"} className="rounded-xl bg-zinc-700 px-4 text-sm font-semibold text-white disabled:opacity-50">
            {busy === "find" ? "…" : "Cari"}
          </button>
        </div>
        {found && !found.found && (
          <p data-testid="reset-not-found" className="mt-2 text-xs text-rose-300">Email <b>{found.email}</b> tidak terdaftar.</p>
        )}
        {found?.found && (
          <div className="mt-3 rounded-xl border border-zinc-700 bg-zinc-950 p-3" data-testid="reset-user-found">
            <p className="text-xs text-zinc-300">
              <span className="font-semibold text-white">{found.email}</span> · peran {found.role}{found.isTestAccount ? " · TEST" : ""}
            </p>
            <button data-testid="reset-make-link-btn" onClick={makeResetLink} disabled={busy === "reset"} className="mt-2 h-9 rounded-full bg-emerald-600 px-4 text-xs font-bold text-white hover:bg-emerald-700 disabled:opacity-50">
              {busy === "reset" ? "Membuat…" : "Buat Tautan Reset"}
            </button>
          </div>
        )}
        {resetInfo && (
          <div className="mt-3 rounded-xl border border-emerald-600/40 bg-emerald-500/10 p-3" data-testid="reset-link-box">
            <p className="text-[11px] text-emerald-200">
              Tautan reset untuk <b>{resetInfo.email}</b> (berlaku 60 menit, sekali pakai). Salin & kirim manual:
            </p>
            <div className="mt-2 flex gap-2">
              <input
                data-testid="reset-link-output"
                readOnly
                value={resetInfo.link}
                onFocus={(e) => e.target.select()}
                className="h-9 flex-1 rounded-lg border border-zinc-700 bg-zinc-950 px-2 font-mono text-[11px] text-emerald-300 outline-none"
              />
              <button data-testid="reset-copy-btn" onClick={copyResetLink} className="flex h-9 items-center gap-1 rounded-lg bg-zinc-700 px-3 text-xs font-semibold text-white">
                <Copy className="h-3.5 w-3.5" /> Salin
              </button>
            </div>
            {!resetInfo.emailConfigured ? (
              <p data-testid="reset-email-warning" className="mt-2 text-[11px] font-semibold text-amber-300">
                ⚠ RESEND_API_KEY tidak ter-set di server ini — email otomatis TIDAK dikirim. Bagikan tautan di atas secara manual.
              </p>
            ) : (
              <p className="mt-2 text-[11px] text-zinc-400">Email juga dikirim otomatis. Jika tidak masuk, bagikan tautan di atas manual.</p>
            )}
          </div>
        )}
      </div>

      <div className="mb-3 flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
          <input
            data-testid="role-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") loadUsers(q); }}
            placeholder="Cari email…"
            className="h-10 w-full rounded-xl border border-zinc-700 bg-zinc-900 pl-9 pr-3 text-sm text-white outline-none"
          />
        </div>
        <button onClick={() => loadUsers(q)} data-testid="role-search-btn" className="rounded-xl bg-zinc-700 px-4 text-sm font-semibold text-white">Cari</button>
      </div>

      {selected.size > 0 && (
        <div className="mb-3 flex items-center gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2" data-testid="bulk-toolbar">
          <span className="text-xs text-amber-200">{selected.size} dipilih</span>
          <button data-testid="bulk-mark-test" onClick={() => setBulkConfirm(true)} className="ml-auto rounded-full bg-amber-500 px-3 py-1 text-[11px] font-bold text-black">Tandai sebagai Test Account</button>
          <button onClick={() => setSelected(new Set())} className="rounded-full px-2 py-1 text-[11px] text-zinc-300">Batal</button>
        </div>
      )}

      <div className="space-y-2">
        {users.length === 0 ? (
          <p className="text-xs text-zinc-500">Tidak ada user.</p>
        ) : users.map((u) => (
          <div key={u.id} data-testid={`role-user-${u.id}`} className="rounded-2xl border border-zinc-700 bg-zinc-800/50 p-3">
            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                data-testid={`select-${u.id}`}
                checked={selected.has(u.id)}
                onChange={() => toggleSelect(u.id)}
                className="mt-1 h-4 w-4 accent-amber-500"
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-white">{u.email}</p>
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${ROLE_STYLE[u.role] || ROLE_STYLE.user}`}>{u.role}</span>
                  {u.isTestAccount && <span className="rounded-full bg-purple-500/30 px-2 py-0.5 text-[10px] font-bold text-purple-200">TEST ACCOUNT</span>}
                  {u.isTestPremium ? (
                    <span className="rounded-full bg-sky-500/30 px-2 py-0.5 text-[10px] font-bold text-sky-200">TEST PREMIUM · {u.subUntil ? when(u.subUntil) : ""}</span>
                  ) : u.subActive ? (
                    <span className="rounded-full bg-emerald-600/30 px-2 py-0.5 text-[10px] font-bold text-emerald-200">PREMIUM · {u.subSource}{u.subUntil ? " · " + when(u.subUntil) : ""}</span>
                  ) : (
                    <span className="rounded-full bg-zinc-700 px-2 py-0.5 text-[10px] font-bold text-zinc-400">Non-premium</span>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-2 flex flex-wrap gap-1">
              {ROLES.map((r) => (
                <button
                  key={r}
                  data-testid={`set-role-${u.id}-${r}`}
                  disabled={busy === u.id || r === u.role}
                  onClick={() => changeRole(u, r)}
                  className={`rounded-lg px-2.5 py-1 text-[11px] font-semibold transition-colors disabled:opacity-40 ${r === u.role ? "bg-zinc-600 text-white" : "bg-zinc-900 text-zinc-300 hover:bg-zinc-700"}`}
                >
                  {r === "super_admin" ? "super" : r}
                </button>
              ))}
              <button
                data-testid={`detail-${u.id}`}
                onClick={() => openDetail(u)}
                className="rounded-lg bg-zinc-900 px-2.5 py-1 text-[11px] font-semibold text-zinc-300 hover:bg-zinc-700"
              >
                Detail
              </button>
              <button
                data-testid={`toggle-test-${u.id}`}
                onClick={() => setTest([u.id], !u.isTestAccount)}
                className={`rounded-lg px-2.5 py-1 text-[11px] font-semibold ${u.isTestAccount ? "bg-purple-600 text-white" : "bg-zinc-900 text-purple-300 hover:bg-zinc-700"}`}
              >
                {u.isTestAccount ? "Lepas Test" : "Jadikan Test"}
              </button>
              {u.isTestAccount && (
                <button
                  data-testid={`test-premium-${u.id}`}
                  onClick={() => grantTestPremium(u)}
                  className="rounded-lg bg-sky-600/30 px-2.5 py-1 text-[11px] font-semibold text-sky-200 hover:bg-sky-600/50"
                >
                  + Test Premium
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-5 rounded-2xl border border-zinc-700 bg-zinc-800/50 p-4">
        <div className="mb-2 flex items-center gap-2">
          <History className="h-4 w-4 text-zinc-400" />
          <p className="font-heading text-sm font-bold text-white">Log Audit Peran</p>
        </div>
        <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
          <input
            data-testid="audit-search"
            value={auditQ}
            onChange={(e) => setAuditQ(e.target.value)}
            placeholder="Cari email…"
            className="h-9 rounded-lg border border-zinc-700 bg-zinc-900 px-3 text-xs text-white outline-none"
          />
          <input
            data-testid="audit-from"
            type="date"
            value={auditFrom}
            onChange={(e) => setAuditFrom(e.target.value)}
            className="h-9 rounded-lg border border-zinc-700 bg-zinc-900 px-3 text-xs text-white outline-none"
          />
          <input
            data-testid="audit-to"
            type="date"
            value={auditTo}
            onChange={(e) => setAuditTo(e.target.value)}
            className="h-9 rounded-lg border border-zinc-700 bg-zinc-900 px-3 text-xs text-white outline-none"
          />
        </div>
        {filteredAudit.length === 0 ? (
          <p className="text-xs text-zinc-500">{audit.length === 0 ? "Belum ada perubahan peran." : "Tidak ada yang cocok dengan filter."}</p>
        ) : (
          <ul className="space-y-2">
            {filteredAudit.map((a) => (
              <li key={a.id} data-testid={`audit-${a.id}`} className="text-xs text-zinc-300">
                <b>{a.actorEmail}</b> mengubah <b>{a.targetEmail}</b>: {a.fromRole} → <span className="text-amber-300">{a.toRole}</span>
                <span className="ml-1 text-zinc-500">· {when(a.createdAt)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Active subscriptions */}
      <div className="mt-5 rounded-2xl border border-zinc-700 bg-zinc-800/50 p-4" data-testid="active-subs-panel">
        <div className="mb-2 flex items-center gap-2">
          <Users className="h-4 w-4 text-emerald-400" />
          <p className="font-heading text-sm font-bold text-white">Langganan Aktif</p>
        </div>
        {subs === null ? (
          <p className="text-xs text-zinc-500">Memuat…</p>
        ) : (
          <>
            <div className="mb-3 flex flex-wrap gap-2">
              <span className="rounded-full bg-zinc-900/60 px-2.5 py-1 text-[11px] font-bold text-white" data-testid="subs-total">Total: {subs.total}</span>
              <span className="rounded-full bg-emerald-500/15 px-2.5 py-1 text-[11px] text-emerald-300">Berbayar: {subs.counts?.subscription ?? 0}</span>
              <span className="rounded-full bg-sky-500/15 px-2.5 py-1 text-[11px] text-sky-300">Voucher: {subs.counts?.voucher ?? 0}</span>
              <span className="rounded-full bg-amber-500/15 px-2.5 py-1 text-[11px] text-amber-300">Trial: {subs.counts?.trial ?? 0}</span>
              <span className="rounded-full bg-fuchsia-500/15 px-2.5 py-1 text-[11px] text-fuchsia-300">Test: {subs.counts?.test ?? 0}</span>
            </div>
            {subs.subscriptions.length === 0 ? (
              <p className="text-xs text-zinc-500">Belum ada langganan aktif.</p>
            ) : (
              <ul className="max-h-72 space-y-2 overflow-y-auto">
                {subs.subscriptions.map((s, i) => (
                  <li key={`${s.email}-${i}`} data-testid={`active-sub-${i}`} className="flex items-center justify-between gap-2 rounded-lg bg-zinc-900/60 px-3 py-2 text-xs">
                    <div className="min-w-0">
                      <p className="truncate font-medium text-zinc-100">{s.email}</p>
                      <p className="text-[11px] text-zinc-500">
                        <span className="text-emerald-300">{s.sourceLabel}</span>
                        {s.reference ? ` · ${s.reference}` : ""}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <div className="text-right">
                        <p className="text-[11px] text-zinc-300">s/d {when(s.until)}</p>
                        <p className="text-[11px] text-zinc-500">{s.daysLeft != null ? `${s.daysLeft} hari lagi` : ""}</p>
                      </div>
                      <button
                        data-testid={`revoke-premium-${i}`}
                        onClick={() => revokePremium(s)}
                        className="rounded-full border border-rose-500/40 px-2.5 py-1 text-[11px] font-semibold text-rose-300 hover:bg-rose-500/10"
                      >
                        Cabut
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>

      {/* Denied admin-access attempts */}
      <div className="mt-5 rounded-2xl border border-zinc-700 bg-zinc-800/50 p-4" data-testid="admin-attempts-panel">
        <div className="mb-2 flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-rose-400" />
          <p className="font-heading text-sm font-bold text-white">Percobaan Akses Admin Ditolak</p>
        </div>
        <p className="mb-3 text-[11px] text-zinc-500">Akun non-admin yang mencoba membuka halaman admin (30 hari terakhir).</p>
        {attempts.length === 0 ? (
          <p className="text-xs text-zinc-500">Belum ada percobaan akses.</p>
        ) : (
          <ul className="space-y-2">
            {attempts.map((a) => (
              <li key={a.id} data-testid={`attempt-${a.id}`} className="flex items-center justify-between gap-2 rounded-lg bg-zinc-900/60 px-3 py-2 text-xs">
                <div className="min-w-0">
                  <p className="truncate font-medium text-rose-200">{a.email || "—"}</p>
                  <p className="text-[11px] text-zinc-500">{a.path}{a.ip ? ` · ${a.ip}` : ""}</p>
                </div>
                <span className="shrink-0 text-[11px] text-zinc-500">{when(a.createdAt)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Subscription detail modal */}
      {detail && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-6" data-testid="sub-detail" onClick={() => setDetail(null)}>
          <div className="w-full max-w-sm rounded-2xl border border-zinc-700 bg-zinc-900 p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <p className="font-heading text-sm font-bold text-white">Riwayat Langganan</p>
              <button onClick={() => setDetail(null)} className="text-zinc-400 text-lg leading-none">×</button>
            </div>
            <p className="mt-1 truncate text-xs text-zinc-400">{detail.user.email}</p>
            {!detail.data ? (
              <p className="py-6 text-center text-xs text-zinc-500">Memuat…</p>
            ) : (
              <>
                <div className="mt-3 rounded-xl border border-zinc-700 p-3 text-xs">
                  <span className="text-zinc-400">Status saat ini: </span>
                  {detail.data.current.active ? (
                    <span className="font-bold text-emerald-300">
                      {detail.data.current.source === "test" ? "TEST PREMIUM" : `AKTIF · ${detail.data.current.source || "-"}`}
                      {detail.data.current.until ? ` · ${when(detail.data.current.until)}` : ""}
                    </span>
                  ) : <span className="font-bold text-zinc-400">Non-premium</span>}
                </div>
                {detail.data.current.active && detail.data.current.source === "test" && (
                  <button
                    data-testid="revoke-test-premium"
                    onClick={() => revokeTestPremium(detail.user)}
                    className="mt-3 w-full rounded-full bg-rose-600/20 py-2.5 text-xs font-bold text-rose-300 hover:bg-rose-600/40"
                  >
                    Cabut TEST PREMIUM sekarang
                  </button>
                )}
                <ul className="mt-3 max-h-64 space-y-2 overflow-y-auto" data-testid="sub-detail-items">
                  {detail.data.items.length === 0 ? (
                    <li className="text-xs text-zinc-500">Belum ada riwayat.</li>
                  ) : detail.data.items.map((it, i) => (
                    <li key={i} className="flex items-center justify-between gap-2 rounded-lg bg-zinc-800/60 px-3 py-2 text-xs">
                      <div className="min-w-0">
                        <p className="font-medium text-zinc-200">{it.label}</p>
                        <p className="text-[11px] text-zinc-500">{it.date ? when(it.date) : "—"}{it.reference ? ` · ${it.reference}` : ""}</p>
                      </div>
                      <span className={`shrink-0 font-mono font-bold ${it.amount > 0 ? "text-white" : "text-emerald-400"}`}>
                        {it.amount > 0 ? `Rp${it.amount.toLocaleString("id-ID")}` : "Rp0"}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
      )}

      {/* Bulk test-account confirmation */}
      {bulkConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-6" data-testid="bulk-confirm">
          <div className="w-full max-w-sm rounded-2xl border border-zinc-700 bg-zinc-900 p-5">
            <p className="font-heading text-base font-bold text-white">Tandai sebagai Test Account?</p>
            <p className="mt-2 text-sm text-zinc-300">Tandai <b>{selected.size}</b> akun terpilih sebagai Test Account? Akun test dikecualikan dari analitik bisnis.</p>
            <div className="mt-5 flex gap-2">
              <button onClick={() => setBulkConfirm(false)} className="flex-1 rounded-full border border-zinc-600 py-2.5 text-sm font-semibold text-zinc-300">Batal</button>
              <button data-testid="bulk-confirm-ok" onClick={() => setTest([...selected], true)} className="flex-1 rounded-full bg-amber-500 py-2.5 text-sm font-bold text-black">Ya, tandai</button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation dialog */}
      {confirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-6" data-testid="role-confirm">
          <div className="w-full max-w-sm rounded-2xl border border-zinc-700 bg-zinc-900 p-5">
            <p className="font-heading text-base font-bold text-white">Ubah peran?</p>
            <p className="mt-2 text-sm text-zinc-300">
              <b className="break-all">{confirm.user.email}</b><br />
              <span className="text-zinc-400">{confirm.user.role}</span> → <span className="font-bold text-amber-300">{confirm.role}</span>
            </p>
            <div className="mt-5 flex gap-2">
              <button
                data-testid="role-confirm-cancel"
                onClick={() => setConfirm(null)}
                className="flex-1 rounded-full border border-zinc-600 py-2.5 text-sm font-semibold text-zinc-300"
              >
                Batal
              </button>
              <button
                data-testid="role-confirm-ok"
                onClick={doChange}
                className="flex-1 rounded-full bg-emerald-600 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700"
              >
                Ya, ubah
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
